/**
 * /policies/shifts/roster — M3X.ATTENDANCE F.4 roster planning.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../components/ui";

interface RosterCell {
  position_id: string;
  on_date: string;
  shift_id: string | null;
  shift_code: string | null;
  source: string;
}

export function ShiftRosterPage() {
  const { t } = useTranslation();
  const today = new Date();
  const in7days = new Date();
  in7days.setDate(today.getDate() + 7);
  const [from, setFrom] = useState(today.toISOString().slice(0, 10));
  const [to, setTo] = useState(in7days.toISOString().slice(0, 10));
  const [posIdsRaw, setPosIdsRaw] = useState("");
  const [cells, setCells] = useState<RosterCell[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function fetchRoster() {
    setErr(null);
    setLoading(true);
    setCells([]);
    try {
      const ids = posIdsRaw
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const qs = new URLSearchParams();
      ids.forEach((id) => qs.append("position_ids", id));
      qs.append("date_from", from);
      qs.append("date_to", to);
      const res = await fetch(`/api/shift-roster?${qs.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setCells(j.cells);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }

  // Pivot for grid display: dates as columns, positions as rows
  const dates = Array.from(new Set(cells.map((c) => c.on_date))).sort();
  const positions = Array.from(new Set(cells.map((c) => c.position_id)));
  const lookup = new Map<string, RosterCell>();
  cells.forEach((c) => lookup.set(`${c.position_id}|${c.on_date}`, c));

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("shift_roster.title", "Shift Roster")}
        subtitle={t(
          "shift_roster.subtitle",
          "F.4 — per-day shift assignment grid. Resolves rotation → single → unassigned.",
        )}
      />
      <div className="rounded-lg border border-slate-200 bg-surface-default p-4">
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <label className="flex flex-col text-xs">
            <span>Position IDs (comma-separated UUIDs)</span>
            <textarea
              value={posIdsRaw}
              onChange={(e) => setPosIdsRaw(e.target.value)}
              className="mt-1 min-w-[400px] rounded border border-border-default p-2 font-mono text-xs"
              rows={2}
            />
          </label>
          <label className="flex flex-col text-xs">
            <span>From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 rounded border border-border-default p-2"
            />
          </label>
          <label className="flex flex-col text-xs">
            <span>To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 rounded border border-border-default p-2"
            />
          </label>
          <button
            type="button"
            onClick={fetchRoster}
            disabled={loading || !posIdsRaw.trim()}
            className="rounded bg-brand-blue px-3 py-2 text-white disabled:opacity-50"
          >
            {loading
              ? t("common.loading", "Loading…")
              : t("policies.shifts_roster_build", "Build roster")}
          </button>
        </div>
      </div>

      {cells.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface-default">
          <table className="w-full text-xs">
            <thead className="bg-surface-muted">
              <tr>
                <th className="px-2 py-1 text-start">Position</th>
                {dates.map((d) => (
                  <th key={d} className="px-2 py-1 font-mono">
                    {d.slice(5)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {positions.map((pos) => (
                <tr key={pos} className="border-t border-border-subtle">
                  <td className="px-2 py-1 font-mono">{pos.slice(0, 8)}</td>
                  {dates.map((d) => {
                    const c = lookup.get(`${pos}|${d}`);
                    return (
                      <td key={d} className="px-2 py-1 text-center">
                        {c?.source === "unassigned" ? (
                          <span className="text-text-tertiary">—</span>
                        ) : (
                          <span
                            className={
                              c?.source === "rotation"
                                ? "rounded bg-brand-blue/10 px-1.5 py-0.5 text-brand-blue"
                                : "rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-800"
                            }
                          >
                            {c?.shift_code ?? "?"}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {err && (
        <div className="rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
          {err}
        </div>
      )}
    </div>
  );
}

export default ShiftRosterPage;
